'use client'

import { useRef, useEffect, useState } from 'react'
import type { SuburbPerformanceData } from '@/lib/feedbridge/types'

function formatCurrency(value: number): string {
  if (value >= 1_000_000) {
    const m = value / 1_000_000
    return `$${m.toFixed(m >= 10 ? 1 : 2)}M`
  }
  if (value >= 1_000) {
    return `$${Math.round(value / 1_000).toLocaleString()}K`
  }
  return `$${value.toLocaleString()}`
}

function getChartData(data: SuburbPerformanceData | undefined) {
  if (!data?.series?.seriesInfo) return []
  return [...data.series.seriesInfo]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((e) => ({ year: e.year, value: e.values.medianSoldPrice ?? null }))
}

const PADDING_TOP = 28
const PADDING_LEFT = 20
const PADDING_RIGHT = 40

interface PriceTrendsChartProps {
  data?: SuburbPerformanceData
  label: string
}

export function PriceTrendsChart({ data, label }: PriceTrendsChartProps) {
  const [containerWidth, setContainerWidth] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width)
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const chartData = getChartData(data)
  const validPoints = chartData.filter(
    (d): d is { year: number; value: number } => d.value !== null,
  )
  if (validPoints.length === 0) return null

  const values = validPoints.map((d) => d.value)
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)

  const range = maxValue - minValue || maxValue * 0.1
  const yMin = Math.max(0, minValue - range * 0.15)
  const yMax = maxValue + range * 0.15

  const isMobile = containerWidth > 0 && containerWidth < 500
  const plotHeight = isMobile ? 200 : 320
  const paddingRight = isMobile ? 10 : PADDING_RIGHT
  const plotWidth = Math.max(containerWidth - PADDING_LEFT - paddingRight, 100)

  const points = validPoints.map((d, i) => {
    const x =
      PADDING_LEFT +
      (validPoints.length > 1 ? (i / (validPoints.length - 1)) * plotWidth : plotWidth / 2)
    const y = PADDING_TOP + plotHeight - ((d.value - yMin) / (yMax - yMin)) * plotHeight
    return { ...d, x, y }
  })

  const plotBottom = PADDING_TOP + plotHeight
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${plotBottom} L ${points[0].x} ${plotBottom} Z`

  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const value = yMin + ((yMax - yMin) * i) / 4
    const y = PADDING_TOP + plotHeight - (i / 4) * plotHeight
    return { value, y }
  })

  const svgHeight = PADDING_TOP + plotHeight
  const svgWidth = containerWidth || 100

  return (
    <div>
      {/* Graph title — Figma 4114:173028 is Headings/headlineMd: display (Merriweather)
          400 at font/size/xl with letterSpacing -0.3, i.e. `text-display-xl`, which
          already carries the responsive 24/25/32 scale. mb-8 = gap/gap-8 32. */}
      <h3 className="font-display font-normal text-display-xl text-foreground mb-8">
        Median Sale Price
      </h3>

      <div ref={containerRef} className="relative" style={{ paddingBottom: '40px' }}>
        {containerWidth > 0 && (
          <svg width={svgWidth} height={svgHeight} className="block">
            {yTicks.map((tick) => (
              <line
                key={tick.value}
                x1={PADDING_LEFT}
                x2={svgWidth - paddingRight}
                y1={tick.y}
                y2={tick.y}
                stroke="var(--color-border)"
                strokeWidth="0.5"
              />
            ))}
            <path d={areaPath} fill="var(--color-foreground)" opacity="0.04" />
            <path
              d={linePath}
              fill="none"
              stroke="var(--color-foreground)"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            {points.map((p) => (
              <g key={p.year}>
                <circle cx={p.x} cy={p.y} r="3" fill="var(--color-foreground)" />
                <circle cx={p.x} cy={p.y} r="1.5" fill="var(--color-background)" />
              </g>
            ))}
          </svg>
        )}

        {containerWidth > 0 &&
          points.map((p, i) => (
            <span
              key={`val-${p.year}`}
              // Price label — Figma 4114:173071 is Body/bodyXs (12/14).
              className="absolute text-body-xs font-normal text-foreground leading-none whitespace-nowrap"
              style={{
                left: `${p.x}px`,
                top: `${p.y - (isMobile && i % 2 === 1 ? 28 : 16)}px`,
                transform: 'translateX(-50%)',
              }}
            >
              {formatCurrency(p.value)}
            </span>
          ))}

        {containerWidth > 0 &&
          points.map((p) => (
            <span
              key={`yr-${p.year}`}
              // Year axis label — Figma 4114:173057 is Body/bodySm (14/16), light.
              className="absolute text-body-sm font-light text-muted-foreground leading-none"
              style={{
                left: `${p.x}px`,
                top: `${PADDING_TOP + plotHeight + 14}px`,
                transform: 'translateX(-50%)',
              }}
            >
              {p.year}
            </span>
          ))}
      </div>
    </div>
  )
}
